import { useGetPVSystemSchema } from '@/components/Account/PVSystem';
import { DocumentPreview, DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { getPreviewUrl, getOriginalUrl } from '@/helpers/documentHelpers';
import { useGetAccountQuery } from '@/services/accounts';
import { useGetDocumentByAccountAndDocumentIdQuery } from '@/services/documents';
import { useRequestAccountDocumentExtractionMutation } from '@/services/genai';
import { useCreateSystemDesignMutation, useGetCurrentDesignQuery } from '@/services/systemDesign';
import { Box, Button, toast, Accordion, useOpenClose, Icon } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useState } from 'react';

import { InstallDocumentTypes, MonthNamesShort, PanelOrientation } from 'types';

import { DocumentRendererProps } from './DocumentRendererRegistry';
import { SummaryRow } from './SummaryRow';

export const ShadeReportRenderer: React.FC<DocumentRendererProps> = ({
  modelOutput,
  accountId,
  isOpen,
  onToggle,
}: DocumentRendererProps) => {
  const { data: account } = useGetAccountQuery(accountId);
  const programType = account?.programType;
  const [createSystemDesign, { isLoading: isCreatingSystemDesign }] = useCreateSystemDesignMutation();
  const documentId = modelOutput?.references?.documentId ?? modelOutput?.references?.documentIds?.[0] ?? '';
  const documentLayout = modelOutput?.response?.documentLayout;
  const { data: document } = useGetDocumentByAccountAndDocumentIdQuery(
    {
      accountId,
      documentId,
    },
    { skip: !documentId },
  );
  const { data: systemDesign } = useGetCurrentDesignQuery({ id: accountId, programType }, { skip: !programType });
  const [requestExtraction] = useRequestAccountDocumentExtractionMutation();
  const validationSchema = useGetPVSystemSchema();
  const [openPanels, setOpenPanels] = useState<Record<number, boolean>>({});
  const [targetDoc, setTargetDoc] = useState<DocumentPreview | null>(null);
  const arrays = modelOutput?.response.arrays || [];

  const {
    isOpen: isFilePreviewModalOpen,
    handleOpen: openFilePreviewModal,
    handleClose: closeFilePreviewModal,
  } = useOpenClose();

  // Create DocumentPreview object when document is loaded
  useEffect(() => {
    if (document && document.files?.[0]) {
      const file = document.files[0];
      const viewUrls = file.viewUrls;
      const documentPreview: DocumentPreview = {
        id: `${document.id}`,
        documentId: `${document.id}`,
        url: getPreviewUrl(viewUrls) ?? '',
        originalUrl: getOriginalUrl(viewUrls),
        contentType: file.contentType || 'application/pdf',
        fileName: file.originalName || 'Document',
        type: document.type,
        createdAt: document.meta?.createdAt,
      };
      setTargetDoc(documentPreview);
    }
  }, [document]);

  const requestShadeReport = useCallback(
    () => requestExtraction({ accountId, documentType: InstallDocumentTypes.shadeReport }),
    [accountId, requestExtraction],
  );

  const saveArraysToDesign = useCallback(async () => {
    if (arrays.length === 0) return;
    const formattedArrays = arrays.map((array: any) => ({
      panelCount: array.panelCount || 0,
      annualTSRF: (array.annualTsrf || 0) * 100,
      orientation: array.orientation || PanelOrientation.combination,
      tilt: array.tiltDegrees || 0,
      azimuth: array.azimuthDegrees || 0,
      monthlySolarAccess: array.monthlySolarAccess.map((monthObj: any) => ({
        month: monthObj.month,
        accessPercent: (monthObj.solarAccess || 0) * 100.0,
      })),
    }));
    const newSystemDesign = {
      systemDesign: { ...systemDesign, arrays: formattedArrays },
      accountId: account.id,
      programType,
    };
    try {
      await validationSchema.validate(newSystemDesign.systemDesign, { abortEarly: false });
      await createSystemDesign(newSystemDesign).unwrap();
      toast.success('Successfully saved arrays to design');
    } catch (error: any) {
      console.error('Failed to save arrays from genai shade report extraction to design', error);
      toast.error(error?.data?.message ?? 'Failed to save arrays to design');
    }
  }, [arrays, createSystemDesign, account, systemDesign, validationSchema]);

  return (
    <Accordion.Panel key={modelOutput._id?.toString()} isOpen={isOpen}>
      <Accordion.PanelSummary hasCaret="right" onToggle={onToggle} isDetailsOpen={isOpen}>
        <Box direction="row" justifyContent="space-between" width="100%" gap="lg" alignItems="baseline">
          <Box fontSize="sm" fontWeight="bold">
            Solar Arrays
          </Box>
          {modelOutput.references?.systemDesignId ? (
            <Box fontSize="sm" fontWeight="medium" justifyContent="flex-end" direction="row" gap="xs">
              Done <Icon name="check" color="success-500" />
            </Box>
          ) : (
            modelOutput.expectedOutputs &&
            modelOutput.score > 0 && (
              <Box fontSize="sm" fontWeight="medium" justifyContent="flex-end">
                {modelOutput.score === 1 ? (
                  <Icon name="check" color="success-500" />
                ) : (
                  `${(modelOutput.score * 100).toFixed(2)} % Match`
                )}
              </Box>
            )
          )}
        </Box>
      </Accordion.PanelSummary>
      <Accordion.PanelDetails>
        <Box gap="md">
          {arrays.length === 0 ? (
            <>
              <Box>No array data found in shade report.</Box>
              <Box>
                <Button variant="tertiary" tone="primary" size="md" onClick={requestShadeReport}>
                  Request Shade Report
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box gap="sm" padding="md" borderWidth="xs" borderColor="grey-200" radius="md" marginBottom="md">
                <Box fontSize="md" fontWeight="bold" marginBottom="sm">
                  Summary
                </Box>

                <SummaryRow
                  label="Customer:"
                  value={`${modelOutput?.response.customerFirstName || ''} ${modelOutput?.response.customerLastName || ''}`.trim()}
                  expectedValue={`${modelOutput?.expectedOutputs?.customerFirstName || ''} ${modelOutput?.expectedOutputs?.customerLastName || ''}`.trim()}
                />

                <SummaryRow
                  label="Address:"
                  value={[
                    modelOutput?.response.customerAddressStreet,
                    modelOutput?.response.customerAddressCity,
                    modelOutput?.response.customerAddressState,
                    modelOutput?.response.customerAddressZipCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                  expectedValue={[
                    modelOutput?.expectedOutputs?.customerAddressStreet,
                    modelOutput?.expectedOutputs?.customerAddressCity,
                    modelOutput?.expectedOutputs?.customerAddressState,
                    modelOutput?.expectedOutputs?.customerAddressZipCode,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                />

                <SummaryRow
                  label="Panel Count:"
                  value={modelOutput?.response.totalPanelCount}
                  expectedValue={modelOutput?.expectedOutputs?.totalPanelCount}
                />

                <SummaryRow label="Source Document:" value={document?.files?.[0]?.originalName} />
                <SummaryRow
                  label="Upload Date:"
                  value={document?.meta?.createdAt ? new Date(document.meta.createdAt).toLocaleDateString() : ''}
                />
              </Box>
              <Accordion>
                {arrays.map((array: any, idx: number) => (
                  <Accordion.Panel key={idx} isOpen={openPanels[idx]}>
                    <Accordion.PanelSummary
                      hasCaret="right"
                      onToggle={() => {
                        setOpenPanels({ ...openPanels, [idx]: !openPanels[idx] });
                      }}
                      isDetailsOpen={openPanels[idx]}
                    >
                      <Box fontWeight="bold">Array {array.arrayId ?? idx + 1}</Box>
                    </Accordion.PanelSummary>
                    <Accordion.PanelDetails>
                      <Box borderWidth="1px" borderColor="grey-300" radius="md" padding="xs" gap="xs">
                        <SummaryRow label="Panel Count:" value={array.panelCount ?? 'N/A'} />
                        <SummaryRow
                          label="Annual TSRF:"
                          value={array.annualTsrf ? `${(array.annualTsrf * 100).toFixed(1)}%` : 'N/A'}
                        />
                        <SummaryRow label="Orientation:" value={array.orientation ?? 'Portrait'} />
                        <SummaryRow label="Tilt:" value={array.tiltDegrees ? `${array.tiltDegrees}°` : 'N/A'} />
                        <SummaryRow
                          label="Azimuth:"
                          value={array.azimuthDegrees ? `${array.azimuthDegrees}°` : 'N/A'}
                        />
                        <Box margin="sm 0">
                          <Box margin="sm 0" fontWeight="bold">
                            Monthly Solar Access:
                          </Box>
                          <Box gap="xl">
                            {[
                              [0, 1, 2, 3],
                              [4, 5, 6, 7],
                              [8, 9, 10, 11],
                            ].map((monthIndices, rowIndex) => (
                              <Box key={rowIndex} direction="row" gap="xl">
                                {monthIndices.map((monthIndex) => {
                                  const monthObj = array.monthlySolarAccess?.[monthIndex];
                                  return (
                                    <Box
                                      key={monthIndex}
                                      direction="column"
                                      gap="xs"
                                      alignItems="center"
                                      width="25%"
                                    >
                                      <Box fontWeight="medium" textAlign="center">
                                        {MonthNamesShort[monthIndex]}
                                      </Box>
                                      <Box textAlign="center">
                                        {typeof monthObj?.solarAccess === 'number'
                                          ? `${(monthObj.solarAccess * 100).toFixed(1)}%`
                                          : 'N/A'}
                                      </Box>
                                    </Box>
                                  );
                                })}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Accordion.PanelDetails>
                  </Accordion.Panel>
                ))}
              </Accordion>
              <Box direction="row" justifyContent="flex-end" margin="md 0">
                {targetDoc && (
                  <Button
                    iconPrefix="arrows-maximize"
                    variant="tertiary"
                    size="md"
                    tone="neutral"
                    onClick={openFilePreviewModal}
                  />
                )}
                <Button
                  variant="tertiary"
                  tone="primary"
                  size="md"
                  onClick={saveArraysToDesign}
                  isLoading={isCreatingSystemDesign}
                >
                  Push to PV System
                </Button>
              </Box>
              {targetDoc && (
                <DocumentPreviewModal
                  targetDoc={targetDoc}
                  setTargetDoc={setTargetDoc}
                  isFilePreviewModalOpen={isFilePreviewModalOpen}
                  documentList={[targetDoc]}
                  closeFilePreviewModal={closeFilePreviewModal}
                  title="Shade Report"
                  documentLayout={documentLayout}
                />
              )}
            </>
          )}
        </Box>
      </Accordion.PanelDetails>
    </Accordion.Panel>
  );
};
